import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import NavBar from './NavBar';
import { loadCalendlyScripts } from '../utils/calendly';

const contactSchema = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  "name": "Contact Quanturama",
  "description": "Contact Quanturama for Web3 systems, automation bots, pipelines, and decentralized infrastructure.",
  "url": "https://quanturama.com/contact"
};

const HOW_OPTIONS = [
  { value: '', label: 'How did you find this? (optional)' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'blog', label: 'Blog' },
  { value: '{0}', label: '{0}' },
  { value: 'referred', label: 'Referred' },
  { value: 'berlin', label: 'Berlin' },
  { value: 'other', label: 'Other' },
];

const FORMSPREE_ID = 'mbdplwyq';

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', problem: '', how: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadCalendlyScripts();
    window.scrollTo(0, 0);
  }, []);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required.';
    if (!form.email.trim()) e.email = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Enter a valid email.';
    if (!form.problem.trim()) e.problem = 'Tell us what the problem is.';
    return e;
  };

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    if (errors[e.target.name]) setErrors(prev => ({ ...prev, [e.target.name]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const e2 = validate();
    if (Object.keys(e2).length > 0) { setErrors(e2); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, _source: 'quanturama.com/contact' })
      });
      if (res.ok) setSubmitted(true);
      else setErrors({ submit: 'Something went wrong. Please email hello@quanturama.com directly.' });
    } catch {
      setErrors({ submit: 'Something went wrong. Please email hello@quanturama.com directly.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact | Quanturama</title>
        <meta name="description" content="Contact Quanturama for Web3 systems, automation bots, pipelines, and decentralized infrastructure." />
        <link rel="canonical" href="https://quanturama.com/contact" />
        <script type="application/ld+json">{JSON.stringify(contactSchema)}</script>
      </Helmet>

      <NavBar />

      <main className="contact-page">
        <div className="contact-container">
          <div className="contact-main">
            <h1 className="contact-headline">Let's talk about your project.</h1>
            <p className="contact-copy">
              Quanturama takes on a small number of projects at a time. The more context you share upfront, the faster we can assess fit and respond with next steps.
            </p>

            {submitted ? (
              <div className="contact-confirmation">
                <p>
                  Got it. If it's the right fit, you'll hear back within 24 hours with next steps —
                  not a template.
                </p>
              </div>
            ) : (
              <form className="contact-form" onSubmit={handleSubmit} noValidate>
                <div className="form-field">
                  <input
                    type="text"
                    name="name"
                    placeholder="Name"
                    value={form.name}
                    onChange={handleChange}
                    className={errors.name ? 'field-error' : ''}
                  />
                  {errors.name && <span className="field-error-msg">{errors.name}</span>}
                </div>

                <div className="form-field">
                  <input
                    type="email"
                    name="email"
                    placeholder="Email"
                    value={form.email}
                    onChange={handleChange}
                    className={errors.email ? 'field-error' : ''}
                  />
                  {errors.email && <span className="field-error-msg">{errors.email}</span>}
                </div>

                <div className="form-field">
                  <textarea
                    name="problem"
                    placeholder="What's broken, stuck, or not built yet? What have you already tried?"
                    value={form.problem}
                    onChange={handleChange}
                    rows={5}
                    className={errors.problem ? 'field-error' : ''}
                  />
                  {errors.problem && <span className="field-error-msg">{errors.problem}</span>}
                </div>

                <div className="form-field">
                  <select name="how" value={form.how} onChange={handleChange}>
                    {HOW_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                {errors.submit && <span className="field-error-msg">{errors.submit}</span>}
                <button type="submit" className="contact-submit" disabled={submitting}>
                  {submitting ? 'Sending…' : 'Send →'}
                </button>
              </form>
            )}
          </div>

          <aside className="contact-sidebar">
            <div className="sidebar-item">
              <a href="mailto:hello@quanturama.com" className="sidebar-email">
                hello@quanturama.com
              </a>
            </div>
            <div className="sidebar-item">
              <span>Response within 24 hours</span>
            </div>
            <div className="sidebar-item">
              <span>Remote · Async</span>
            </div>
          </aside>
        </div>
      </main>
    </>
  );
};

export default Contact;
